import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase, sendConfirmationEmail } from '../lib/supabase';
import { Service, Slot, BarberShop, Reservation, Profile, Barber } from '../types';
import { format, addDays, startOfDay, isSameDay, parseISO } from 'date-fns';
import { motion, AnimatePresence } from 'motion/react';
import { Calendar as CalendarIcon, Clock, Scissors, CheckCircle2, ChevronRight, User, Mail, Phone, Plus, X, Sparkles, Store, MapPin, History, AlertCircle, UserRound, Users, Navigation, Eye, CalendarPlus, CalendarDays, Star, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../lib/LanguageContext';
import { useTheme } from '../lib/ThemeContext';
import { bookingSchema } from '../lib/validations';
import { z } from 'zod';
import Auth from './Auth';

import { ReviewModal } from './ReviewModal';
import { ConfirmDialog } from './ConfirmDialog';

type BookingFormData = z.infer<typeof bookingSchema>;

export default function BookingFlow({ 
  user, 
  profile,
  mode = 'booking',
  setMode
}: { 
  user: any, 
  profile: Profile | null,
  mode?: 'booking' | 'history',
  setMode?: (mode: 'booking' | 'history') => void
}) {
  const { t, dateLocale, language } = useLanguage();
  const { theme } = useTheme();
  const [shops, setShops] = useState<BarberShop[]>([]);
  const [selectedShop, setSelectedShop] = useState<BarberShop | null>(null);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [selectedBarber, setSelectedBarber] = useState<Barber | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));
  const [availableSlots, setAvailableSlots] = useState<Slot[]>([]);
  const [allSlots, setAllSlots] = useState<Slot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [step, setStep] = useState(0); // 0: Shop, 1: Barber, 2: Services, 3: Time, 4: Details, 5: Confirmation, 6: Confirmed
  const [myReservations, setMyReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<any>(null);
  const [reviewingReservation, setReviewingReservation] = useState<any>(null);
  const [confirmCancel, setConfirmCancel] = useState<{ resId: string, slotId: string } | null>(null);
  const [viewingDetails, setViewingDetails] = useState<any>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue
  } = useForm<BookingFormData>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      name: profile?.email?.split('@')[0] || '',
      email: profile?.email || '',
      phone: ''
    }
  });

  useEffect(() => {
    if (profile) {
      setValue('name', profile.email?.split('@')[0] || '');
      setValue('email', profile.email || '');
    }
  }, [profile, setValue]);

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    if (selectedShop) {
      fetchBarbers();
      fetchServices();
      fetchSlots();
    }
  }, [selectedShop, selectedDate, selectedBarber]);

  const fetchBarbers = async () => {
    if (!selectedShop) return;
    const { data, error } = await supabase
      .from('barbers')
      .select('*')
      .eq('barber_shop_id', selectedShop.id)
      .order('name', { ascending: true });
    if (error) toast.error(`Failed to load barbers: ${error.message}`);
    else setBarbers(data || []);
  };

  useEffect(() => {
    if (mode === 'history' && user) {
      fetchMyReservations();
    }
  }, [mode, user]);

  const fetchMyReservations = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('reservations')
        .select(`
          *,
          barber_shops (name),
          barbers (name, photo_url),
          reservation_services (service_id, services (name)),
          reservation_slots (slot_id, slots (date, time))
        `)
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMyReservations(data || []);
    } catch (err: any) {
      toast.error(t('booking.noBookings') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadICS = () => {
    if (!selectedSlot || !selectedShop) return;

    const dateStr = selectedSlot.date.replace(/-/g, '');
    const startTimeStr = selectedSlot.time.replace(':', '') + '00';
    const endTimeStr = '235900'; 

    const dtStart = `${dateStr}T${startTimeStr}`;
    const desc = selectedServices.map(s => s.name).join(', ');

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//SharpCuts//EN
BEGIN:VEVENT
UID:${selectedSlot.id}@sharpcuts.com
DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DTSTART;TZID=Europe/Zurich:${dtStart}
SUMMARY:${selectedShop.name} - Haircut
DESCRIPTION:Appointment at ${selectedShop.name} for ${desc}.
LOCATION:${selectedShop.address}
BEGIN:VALARM
TRIGGER:-PT30M
ACTION:DISPLAY
DESCRIPTION:Reminder
END:VALARM
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `appointment-${selectedShop.name.replace(/\s+/g, '-')}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCancelBooking = async (reservationId: string, slotId: string) => {
    setLoading(true);
    try {
      // 1. Update Reservation Status
      const { error: resError } = await supabase
        .from('reservations')
        .update({ status: 'cancelled' })
        .eq('id', reservationId);
      
      if (resError) throw resError;

      // 2. Make Slots Available Again
      // Fetch all slots associated with this reservation
      const { data: resSlots, error: fetchSlotsError } = await supabase
        .from('reservation_slots')
        .select('slot_id')
        .eq('reservation_id', reservationId);
      
      if (fetchSlotsError) throw fetchSlotsError;
      
      const slotIds = resSlots?.map(rs => rs.slot_id) || [slotId];
      if (slotIds.length > 0) {
        const { error: slotError } = await supabase
          .from('slots')
          .update({ is_available: true })
          .in('id', slotIds);
        
        if (slotError) throw slotError;
      }
      
      // 3. Send Notification (Mocked)
      try {
        const reservation = myReservations.find(r => r.id === reservationId);
        const shopName = reservation?.barber_shops?.name || 'Sharp Cuts';
        const customerName = reservation?.customer_name || user.email?.split('@')[0];
        const slot = reservation?.reservation_slots?.[0]?.slots;
        const dateStr = slot ? format(parseISO(slot.date), 'PPP', { locale: dateLocale }) : '';
        const timeStr = slot ? slot.time.substring(0, 5) : '';

        await sendConfirmationEmail(user.email, 'cancelled', {
          shopName,
          customerName,
          date: dateStr,
          time: timeStr
        });
      } catch (emailError) {
        console.warn('Email notification failed but cancellation was successful:', emailError);
      }

      toast.success(t('common.status') + ': ' + t('common.cancelled'));
      fetchMyReservations();
    } catch (err: any) {
      toast.error(t('common.status') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  const fetchShops = async () => {
    const { data, error } = await supabase.from('barber_shops').select('*').order('name', { ascending: true });
    if (error) {
      toast.error(`Failed to load shops: ${error.message}`);
    } else {
      let activeShops = data?.filter(shop => shop.is_active !== false) || [];
      // Set initial shops (sorted by name as per query)
      setShops(activeShops);
      
      // Try to get user location to refine sorting
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            
            const shopsWithDistance = activeShops.map(shop => {
              if (shop.latitude && shop.longitude) {
                return {
                  ...shop,
                  distance: calculateDistance(latitude, longitude, shop.latitude, shop.longitude)
                };
              }
              return { ...shop, distance: Infinity };
            });

            const sortedShops = [...shopsWithDistance].sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
            setShops(sortedShops);
            
            // Only auto-select if we haven't already selected a shop
            if (sortedShops.length === 1 && !selectedShop) {
              setSelectedShop(sortedShops[0]);
              setStep(1);
            }
          },
          (geoError) => {
            console.warn('Geolocation error:', geoError.message);
            // Fallback to name-sorted list already set
          },
          { timeout: 5000, enableHighAccuracy: true }
        );
      }
    }
  };

  const fetchServices = async () => {
    if (!selectedShop) return;
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('barber_shop_id', selectedShop.id)
      .order('name', { ascending: true });
    if (error) toast.error(`Failed to load services: ${error.message}`);
    else setServices(data || []);
  };

  const fetchSlots = async () => {
    if (!selectedShop) return;
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    let query = supabase
      .from('slots')
      .select('*')
      .eq('barber_shop_id', selectedShop.id)
      .eq('date', dateStr)
      .order('time', { ascending: true });

    if (selectedBarber) {
      query = query.eq('barber_id', selectedBarber.id);
    }

    const { data, error } = await query;

    if (error) toast.error(`Failed to load slots: ${error.message}`);
    else setAllSlots(data || []);
  };

  const toggleService = (service: Service) => {
    setSelectedServices(prev => 
      prev.find(s => s.id === service.id)
        ? prev.filter(s => s.id !== service.id)
        : [...prev, service]
    );
  };

  const handleApplyPromo = async () => {
    if (!promoCode || !selectedShop) return;
    
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .eq('shop_id', selectedShop.id)
        .eq('code', promoCode.toUpperCase())
        .eq('active', true)
        .single();

      if (error || !data) {
        toast.error(t('booking.invalidPromo'));
        return;
      }

      if (data.expires_at && new Date(data.expires_at) < new Date()) {
        toast.error(t('booking.invalidPromo'));
        return;
      }

      setAppliedPromo(data);
      toast.success(t('booking.discountApplied'));
    } catch (err) {
      toast.error(t('booking.invalidPromo'));
    }
  };

  const basePrice = selectedServices.reduce((sum, s) => sum + Number(s.price), 0);
  
  const discount = React.useMemo(() => {
    if (!appliedPromo) return 0;
    if (appliedPromo.discount_type === 'percent') {
      return basePrice * (Number(appliedPromo.value) / 100);
    }
    return Number(appliedPromo.value);
  }, [appliedPromo, basePrice]);

  const totalPrice = Math.max(0, basePrice - discount);
  const totalDuration = selectedServices.reduce((sum, s) => sum + Number(s.duration), 0);

  const timeToMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  useEffect(() => {
    if (!selectedShop || allSlots.length === 0) {
      setAvailableSlots([]);
      return;
    }
    
    const closingTime = selectedShop.closing_time || '18:00';
    const closingMinutes = timeToMinutes(closingTime);
    
    // Group slots by barber
    const slotsByBarber: { [key: string]: Slot[] } = {};
    allSlots.forEach(slot => {
      if (!slotsByBarber[slot.barber_id]) {
        slotsByBarber[slot.barber_id] = [];
      }
      slotsByBarber[slot.barber_id].push(slot);
    });

    const validSlots: Slot[] = [];
    const seenTimes = new Set<string>();

    Object.values(slotsByBarber).forEach(barberSlots => {
      // Sort slots by time
      barberSlots.sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

      // Determine the typical interval for this barber
      let interval = 30;
      if (barberSlots.length > 1) {
        const diffs: { [key: number]: number } = {};
        for (let k = 0; k < barberSlots.length - 1; k++) {
          const diff = timeToMinutes(barberSlots[k+1].time) - timeToMinutes(barberSlots[k].time);
          if (diff > 0) diffs[diff] = (diffs[diff] || 0) + 1;
        }
        const sortedDiffs = Object.entries(diffs).sort((a: any, b: any) => b[1] - a[1]);
        if (sortedDiffs.length > 0) interval = Number(sortedDiffs[0][0]);
      }

      for (let i = 0; i < barberSlots.length; i++) {
        if (!barberSlots[i].is_available) continue;
        
        let accumulatedDuration = 0;
        let isValid = false;
        
        for (let j = i; j < barberSlots.length; j++) {
          if (!barberSlots[j].is_available) break;
          
          const currentSlotMinutes = timeToMinutes(barberSlots[j].time);
          const nextSlotMinutes = j + 1 < barberSlots.length 
            ? timeToMinutes(barberSlots[j + 1].time) 
            : closingMinutes;
            
          const actualGap = nextSlotMinutes - currentSlotMinutes;
          
          // If the gap to the next slot is larger than the interval, 
          // it means there's a break or another booking.
          // This slot only contributes 'interval' minutes of availability.
          if (j + 1 < barberSlots.length && actualGap > interval) {
            accumulatedDuration += interval;
            if (accumulatedDuration >= totalDuration) {
              isValid = true;
            }
            break; // Cannot continue through a gap
          }
          
          accumulatedDuration += actualGap;
          
          if (accumulatedDuration >= totalDuration) {
            isValid = true;
            break;
          }
        }
        
        if (isValid) {
          const time = barberSlots[i].time;
          // If we haven't seen this time yet, or if it's for a specific barber, add it
          if (!seenTimes.has(time) || selectedBarber) {
            validSlots.push(barberSlots[i]);
            seenTimes.add(time);
          }
        }
      }
    });
    
    // Sort valid slots by time
    validSlots.sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
    
    setAvailableSlots(validSlots);
    
    // If the currently selected slot is no longer valid, deselect it
    if (selectedSlot && !validSlots.find(s => s.id === selectedSlot.id)) {
      setSelectedSlot(null);
    }
  }, [allSlots, selectedServices, selectedShop, selectedBarber, totalDuration]);

  const handleBooking = async () => {
    if (!selectedSlot || selectedServices.length === 0 || !selectedShop) return;

    setLoading(true);
    try {
      // 1. Create Reservation
      const { data: reservation, error: resError } = await supabase
        .from('reservations')
        .insert([{
          barber_shop_id: selectedShop.id,
          barber_id: selectedBarber?.id || selectedSlot.barber_id,
          customer_id: user?.id || null,
          customer_name: watch('name'),
          customer_email: watch('email'),
          customer_phone: watch('phone'),
          total_price: totalPrice,
          status: 'pending'
        }])
        .select()
        .single();

      if (resError) throw resError;

      // 2. Link Services
      const serviceLinks = selectedServices.map(s => ({
        reservation_id: reservation.id,
        service_id: s.id
      }));
      const { error: linkError } = await supabase.from('reservation_services').insert(serviceLinks);
      if (linkError) throw linkError;

      // Find all slots that are consumed by this booking
      // We must filter allSlots by barber first to ensure we are looking at the right sequence
      const barberSlots = allSlots
        .filter(s => s.barber_id === (selectedBarber?.id || selectedSlot.barber_id))
        .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

      const consumedSlots: Slot[] = [];
      let accumulated = 0;
      const startIndex = barberSlots.findIndex(s => s.id === selectedSlot.id);
      
      if (startIndex !== -1) {
        for (let j = startIndex; j < barberSlots.length; j++) {
          consumedSlots.push(barberSlots[j]);
          const currentSlotMinutes = timeToMinutes(barberSlots[j].time);
          const nextSlotMinutes = j + 1 < barberSlots.length 
            ? timeToMinutes(barberSlots[j + 1].time) 
            : timeToMinutes(selectedShop.closing_time || '18:00');
            
          accumulated += (nextSlotMinutes - currentSlotMinutes);
          if (accumulated >= totalDuration) {
            break;
          }
        }
      }

      // 3. Link Slots
      const slotLinks = consumedSlots.map(s => ({
        reservation_id: reservation.id,
        slot_id: s.id
      }));
      const { error: slotLinkError } = await supabase.from('reservation_slots').insert(slotLinks);
      if (slotLinkError) throw slotLinkError;

      // 4. Mark Slots as Unavailable
      const consumedSlotIds = consumedSlots.map(s => s.id);
      const { error: slotUpdateError } = await supabase
        .from('slots')
        .update({ is_available: false })
        .in('id', consumedSlotIds);

      if (slotUpdateError) throw slotUpdateError;

      // 5. Send Emails
      const dateStr = format(selectedDate, 'PPP', { locale: dateLocale });
      const timeStr = selectedSlot.time.substring(0, 5);
      const serviceNames = selectedServices.map(s => s.name).join(', ');
      const barberName = selectedBarber?.name || 'Any';

      try {
        // Customer Email
        await sendConfirmationEmail(watch('email'), 'confirmed', {
          shopName: selectedShop.name,
          customerName: watch('name'),
          date: dateStr,
          time: timeStr,
          serviceName: serviceNames,
          barberName: barberName
        });

        // Admin Email
        const adminEmail = selectedShop.email || 'devthines@gmail.com';
        await sendConfirmationEmail(adminEmail, 'new_reservation', {
          shopName: selectedShop.name,
          customerName: watch('name'),
          date: dateStr,
          time: timeStr,
          serviceName: serviceNames,
          barberName: barberName,
          phone: watch('phone')
        });
      } catch (emailError) {
        console.warn('Email notification failed but booking was successful:', emailError);
      }

      setStep(6);
      setAcceptedPolicy(false);
      toast.success('Booking submitted! Waiting for confirmation.');
    } catch (err: any) {
      console.error(err);
      toast.error('Booking failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const nextDays = Array.from({ length: 14 }, (_, i) => addDays(new Date(), i));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6">
      {mode === 'booking' ? (
        <>
          {/* Progress Indicator */}
          {step < 6 && (
            <div className="max-w-2xl mx-auto mb-12 sm:mb-16">
              <div className="flex items-center justify-between relative px-2">
                <div className="absolute top-[15px] left-0 w-full h-[2px] bg-zinc-100 dark:bg-zinc-800 -translate-y-1/2 z-0 rounded-full" />
                <div 
                  className="absolute top-[15px] left-0 h-[2px] bg-amber-500 -translate-y-1/2 z-0 transition-all duration-700 ease-in-out rounded-full" 
                  style={{ width: `${(step / 5) * 100}%` }}
                />
                {[0, 1, 2, 3, 4, 5].map((s) => (
                  <div key={s} className="relative z-10 flex flex-col items-center">
                    <button 
                      onClick={() => s < step && setStep(s)}
                      disabled={s >= step}
                      className={`w-8 h-8 rounded-xl border flex items-center justify-center transition-all duration-500 ${
                        step > s 
                          ? 'bg-amber-500 border-amber-500 text-white shadow-lg shadow-amber-500/20 cursor-pointer' 
                          : step === s 
                            ? 'bg-white dark:bg-black border-amber-500 text-amber-500 scale-125 shadow-xl shadow-amber-500/10' 
                            : 'bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-300 dark:text-zinc-600'
                      }`}
                    >
                      {step > s ? <Check size={14} /> : <span className="text-[10px] font-black">{s + 1}</span>}
                    </button>
                    <span className={`absolute -bottom-8 left-1/2 -translate-x-1/2 text-[9px] font-black uppercase tracking-widest w-max transition-all duration-300 ${
                      step === s 
                        ? 'opacity-100 text-amber-500 translate-y-0' 
                        : 'opacity-40 text-zinc-400 translate-y-1'
                    }`}>
                      {s === 0 ? t('booking.stepShop') || 'Shop' :
                       s === 1 ? t('booking.stepBarber') || 'Barber' :
                       s === 2 ? t('booking.stepServices') || 'Service' :
                       s === 3 ? t('booking.stepTime') || 'Time' :
                       s === 4 ? t('booking.stepDetails') || 'Info' :
                       t('booking.stepReview') || 'Review'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div 
            key="step0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="text-center mb-8 sm:mb-12">
              <h2 className={`text-3xl sm:text-3xl font-black uppercase tracking-tighter mb-4 ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{t('booking.selectShop')}</h2>
              <p className={`font-bold text-sm sm:text-base ${theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500'}`}>{t('booking.chooseLocation')}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {shops.map((shop) => (
                <button
                  key={shop.id}
                  onClick={() => {
                    setSelectedShop(shop);
                    setStep(1);
                  }}
                  className={`p-6 sm:p-8 ${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} rounded-[2rem] sm:rounded-[3rem] border shadow-xl hover:border-amber-400 dark:hover:border-amber-500 hover:shadow-2xl transition-all text-left group`}
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="p-3 sm:p-4 bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-white rounded-2xl group-hover:bg-amber-50 dark:group-hover:bg-amber-900/30 group-hover:text-amber-500 transition-colors">
                      <Store size={24} className="sm:w-8 sm:h-8" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{shop.name}</h3>
                      <div className="flex items-center gap-1 text-zinc-400 dark:text-zinc-300 text-xs sm:text-sm font-bold mt-1">
                        <MapPin size={12} className="sm:w-3.5 sm:h-3.5" />
                        {shop.address || t('common.noAddress')}
                      </div>
                      {shop.distance !== undefined && shop.distance !== Infinity && (
                        <div className="flex items-center gap-1 text-amber-500 text-[10px] sm:text-xs font-black uppercase tracking-widest mt-2">
                          <Navigation size={10} className="sm:w-3 sm:h-3" />
                          {shop.distance < 1 
                            ? `${(shop.distance * 1000).toFixed(0)} m ${t('common.away')}`
                            : `${shop.distance.toFixed(1)} km ${t('common.away')}`}
                        </div>
                      )}
                      <div className="flex items-center gap-4 mt-3">
                        <div className="flex items-center gap-1.5 text-[10px] sm:text-xs font-black text-zinc-900 dark:text-zinc-300 group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                          <Clock size={12} className="text-zinc-400 dark:text-zinc-300 sm:w-3.5 sm:h-3.5" />
                          {shop.opening_time || '09:00'} - {shop.closing_time || '18:00'}
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
              {shops.length === 0 && (
                <div className="col-span-full text-center py-12 text-zinc-400 font-bold">
                  {t('admin.allShops')}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div 
            key="step1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className={`flex items-center gap-2 font-bold ${theme === 'dark' ? 'text-zinc-200' : 'text-zinc-500'}`}>
                <Store size={18} /> {selectedShop?.name}
              </div>
              <button 
                onClick={() => {
                  setSelectedShop(null);
                  setSelectedBarber(null);
                  setStep(0);
                }}
                aria-label={t('booking.changeShop')}
                className={`text-xs font-black uppercase tracking-widest transition-colors ${theme === 'dark' ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-900'}`}
              >
                {t('booking.changeShop')}
              </button>
            </div>

            <div className="text-center mb-12">
              <h2 className={`text-3xl font-black uppercase tracking-tighter mb-4 ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{t('booking.selectBarber')}</h2>
              <p className={`font-bold ${theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500'}`}>{t('booking.chooseBarber')}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <button
                onClick={() => {
                  setSelectedBarber(null);
                  setStep(2);
                }}
                className={`p-8 rounded-[3rem] border-2 transition-all text-center group flex flex-col items-center justify-center gap-4 ${
                  selectedBarber === null 
                    ? (theme === 'dark' ? 'bg-white border-white text-zinc-900 shadow-2xl' : 'bg-zinc-900 border-zinc-900 text-white shadow-2xl') 
                    : (theme === 'dark' ? 'bg-zinc-900 border-zinc-800 hover:border-amber-500' : 'bg-white border-zinc-100 hover:border-amber-400')
                }`}
              >
                <div className={`p-4 rounded-2xl ${selectedBarber === null ? (theme === 'dark' ? 'bg-zinc-100 text-amber-600' : 'bg-zinc-800 text-amber-400') : (theme === 'dark' ? 'bg-zinc-800 text-zinc-300 group-hover:bg-amber-900/30 group-hover:text-amber-500' : 'bg-zinc-50 text-zinc-400 group-hover:bg-amber-50 group-hover:text-amber-500')}`}>
                  <Users size={32} aria-hidden="true" />
                </div>
                <div>
                  <h3 className={`text-xl font-black uppercase tracking-tighter ${selectedBarber === null ? (theme === 'dark' ? 'text-zinc-900' : 'text-white') : (theme === 'dark' ? 'text-white' : 'text-zinc-900')}`}>{t('common.any')} {t('common.barber')}</h3>
                  <p className={`text-xs font-bold opacity-60 mt-1 ${selectedBarber === null ? (theme === 'dark' ? 'text-zinc-600' : 'text-zinc-300') : (theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500')}`}>Best available professional</p>
                </div>
              </button>

              {barbers.map((barber) => (
                <button
                  key={barber.id}
                  onClick={() => {
                    setSelectedBarber(barber);
                    setStep(2);
                  }}
                  className={`p-8 rounded-[3rem] border-2 transition-all text-center group flex flex-col items-center justify-center gap-4 ${
                    selectedBarber?.id === barber.id 
                      ? (theme === 'dark' ? 'bg-white border-white text-zinc-900 shadow-2xl' : 'bg-zinc-900 border-zinc-900 text-white shadow-2xl') 
                      : (theme === 'dark' ? 'bg-zinc-900 border-zinc-800 hover:border-amber-500' : 'bg-white border-zinc-100 hover:border-amber-400')
                  }`}
                >
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 shrink-0">
                    {barber.photo_url ? (
                      <img 
                        src={barber.photo_url} 
                        alt={barber.name} 
                        className="w-full h-full object-cover" 
                        referrerPolicy="no-referrer"
                        loading="lazy"
                        width="80"
                        height="80"
                      />
                    ) : (
                      <div className={`w-full h-full flex items-center justify-center ${theme === 'dark' ? 'text-zinc-700' : 'text-zinc-300'}`}>
                        <UserRound size={40} aria-hidden="true" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className={`text-xl font-black uppercase tracking-tighter ${selectedBarber?.id === barber.id ? (theme === 'dark' ? 'text-zinc-900' : 'text-white') : (theme === 'dark' ? 'text-white' : 'text-zinc-900')}`}>{barber.name}</h3>
                    <p className={`text-xs font-bold opacity-60 mt-1 line-clamp-1 ${selectedBarber?.id === barber.id ? (theme === 'dark' ? 'text-zinc-600' : 'text-zinc-100') : (theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500')}`}>{barber.bio}</p>
                  </div>
                </button>
              ))}

              {barbers.length === 0 && (
                <div className="col-span-full text-center py-12 text-zinc-400 font-bold">
                  {t('booking.noBarbers')}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div 
            key="step2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex flex-col gap-1">
                <div className={`flex items-center gap-2 font-bold text-sm ${theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500'}`}>
                  <Store size={14} /> {selectedShop?.name}
                </div>
                {selectedBarber && (
                  <div className="flex items-center gap-2 text-amber-500 font-bold text-sm">
                    <UserRound size={14} /> {selectedBarber.name}
                  </div>
                )}
              </div>
              <button 
                onClick={() => {
                  setSelectedBarber(null);
                  setSelectedServices([]);
                  setStep(1);
                }}
                aria-label={t('booking.changeBarber')}
                className={`text-xs font-black uppercase tracking-widest transition-colors ${theme === 'dark' ? 'text-zinc-300 hover:text-white' : 'text-zinc-400 hover:text-zinc-900'}`}
              >
                {t('booking.changeBarber')}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => {
                const isSelected = selectedServices.find(s => s.id === service.id);
                return (
                  <button
                    key={service.id}
                    onClick={() => toggleService(service)}
                    className={`p-6 rounded-2xl text-left transition-all relative overflow-hidden group border-2 ${
                      isSelected 
                        ? (theme === 'dark' ? 'bg-white border-white text-zinc-900 shadow-2xl scale-[1.02]' : 'bg-zinc-900 border-zinc-900 text-white shadow-2xl scale-[1.02]') 
                        : (theme === 'dark' ? 'bg-zinc-900 border-zinc-800 hover:border-amber-500' : 'bg-white border-zinc-100 hover:border-amber-400')
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className={`p-3 rounded-2xl transition-colors ${
                        isSelected 
                          ? (theme === 'dark' ? 'bg-zinc-100 text-zinc-900' : 'bg-amber-400 text-zinc-900') 
                          : (theme === 'dark' ? 'bg-zinc-800 text-zinc-300 group-hover:bg-amber-900/30 group-hover:text-amber-600' : 'bg-zinc-50 text-zinc-400 group-hover:bg-amber-50 group-hover:text-amber-600')
                      }`}>
                        <Scissors size={24} aria-hidden="true" />
                      </div>
                      <span className={`text-xl font-black ${isSelected ? (theme === 'dark' ? 'text-amber-600' : 'text-amber-400') : (theme === 'dark' ? 'text-white' : 'text-zinc-900')}`}>
                        {t('common.currency')} {service.price}
                      </span>
                    </div>
                    <h3 className={`text-lg font-bold mb-2 ${isSelected ? (theme === 'dark' ? 'text-zinc-900' : 'text-white') : (theme === 'dark' ? 'text-white' : 'text-zinc-900')}`}>{service.name}</h3>
                    <p className={`text-sm mb-4 ${isSelected ? (theme === 'dark' ? 'text-zinc-600' : 'text-zinc-400') : (theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500')}`}>
                      {service.description}
                    </p>
                    <div className="flex items-center gap-2">
                      <div className={`flex items-center text-xs font-bold px-2 py-1 rounded-lg ${
                        isSelected ? (theme === 'dark' ? 'bg-zinc-100 text-zinc-500' : 'bg-zinc-800 text-zinc-400') : (theme === 'dark' ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-50 text-zinc-400')
                      }`}>
                        <Clock size={12} className="mr-1" />
                        {service.duration}m
                      </div>
                      {service.category && (
                        <div className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-lg ${
                          isSelected ? (theme === 'dark' ? 'bg-zinc-100 text-amber-600' : 'bg-zinc-800 text-amber-400') : (theme === 'dark' ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-100 text-zinc-500')
                        }`}>
                          {service.category}
                        </div>
                      )}
                      {isSelected && (
                        <div className="bg-amber-400 text-zinc-900 p-1 rounded-full">
                          <CheckCircle2 size={14} />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
              {services.length === 0 && (
                <div className="col-span-full text-center py-12 text-zinc-400 font-bold">
                  {t('booking.noSlots')}
                </div>
              )}
            </div>

            {selectedServices.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="fixed bottom-8 left-1/2 -translate-x-1/2 w-full max-w-md px-6 z-50"
              >
                <div className="bg-zinc-900 text-white p-6 rounded-2xl shadow-2xl flex items-center justify-between border border-zinc-800">
                  <div>
                    <div className={`font-bold text-zinc-500 mb-1 ${language === 'ta' ? 'text-xs' : 'text-xs uppercase tracking-widest'}`}>{t('booking.totalSelection')}</div>
                    <div className="flex items-baseline gap-3">
                      <div className="text-2xl font-black text-amber-400">{t('common.currency')} {totalPrice}</div>
                      <div className="text-sm font-bold text-zinc-400 dark:text-zinc-300">{totalDuration} {t('common.min')}</div>
                    </div>
                  </div>
                  <button 
                    onClick={() => setStep(3)}
                    className="bg-amber-400 text-zinc-900 px-8 py-3 rounded-2xl font-black flex items-center gap-2 hover:bg-amber-300 transition-colors"
                  >
                    {t('common.continue')} <ChevronRight size={20} />
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {step === 3 && (
          <motion.div 
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-8 pb-24"
          >
            <div className={`${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} p-6 sm:p-8 rounded-[3rem] border shadow-sm`}>
              <h3 className={`font-black text-xl mb-6 flex items-center gap-2 uppercase tracking-tighter ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>
                <CalendarIcon className="text-amber-500" /> {t('booking.selectDate')}
              </h3>
              <div className="flex gap-3 overflow-x-auto pb-4 no-scrollbar">
                {nextDays.map((date) => (
                  <button
                    key={date.toISOString()}
                    onClick={() => setSelectedDate(startOfDay(date))}
                    className={`flex-shrink-0 w-24 py-6 rounded-2xl border-2 transition-all ${
                      isSameDay(selectedDate, date) 
                        ? (theme === 'dark' ? 'bg-white border-white text-zinc-900 shadow-xl' : 'bg-zinc-900 border-zinc-900 text-white shadow-xl') 
                        : (theme === 'dark' ? 'bg-zinc-800 border-zinc-700 hover:border-amber-500 text-white' : 'bg-white border-zinc-50 hover:border-amber-400 text-zinc-900')
                    }`}
                  >
                    <div className={`font-black mb-2 ${theme === 'dark' && isSameDay(selectedDate, date) ? 'text-zinc-900 opacity-70' : (theme === 'dark' ? 'text-white opacity-70' : 'opacity-50')} ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{format(date, 'EEE', { locale: dateLocale })}</div>
                    <div className={`text-2xl font-black ${theme === 'dark' && isSameDay(selectedDate, date) ? 'text-zinc-900' : (theme === 'dark' ? 'text-white' : '')}`}>{format(date, 'd')}</div>
                    <div className={`font-bold mt-1 ${theme === 'dark' && isSameDay(selectedDate, date) ? 'text-zinc-900 opacity-70' : (theme === 'dark' ? 'text-white opacity-70' : 'opacity-50')} ${language === 'ta' ? 'text-xs' : 'text-[10px]'}`}>{format(date, 'MMM', { locale: dateLocale })}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className={`${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} p-6 sm:p-8 rounded-[3rem] border shadow-sm`}>
              <h3 className={`font-black text-xl mb-6 flex items-center gap-2 uppercase tracking-tighter ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>
                <Clock className="text-amber-500" /> {t('booking.availableSlots')}
              </h3>
              {availableSlots.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.id}
                      onClick={() => {
                        setSelectedSlot(slot);
                        setStep(4);
                      }}
                      className={`py-4 rounded-2xl border-2 text-sm font-black transition-all ${
                        selectedSlot?.id === slot.id
                          ? (theme === 'dark' ? 'bg-white border-white text-zinc-900 shadow-lg' : 'bg-zinc-900 border-zinc-900 text-white shadow-lg')
                          : (theme === 'dark' ? 'bg-zinc-800 border-transparent hover:border-amber-500 text-white' : 'bg-zinc-50 border-transparent hover:border-amber-400 text-zinc-900')
                      }`}
                    >
                      {slot.time.substring(0, 5)}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className={`inline-block p-4 rounded-full mb-4 ${theme === 'dark' ? 'bg-zinc-800 text-zinc-600' : 'bg-zinc-50 text-zinc-300'}`}>
                    <Clock size={32} />
                  </div>
                  <p className="text-zinc-400 font-bold">{t('booking.noSlots')}</p>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center">
              <button 
                onClick={() => setStep(2)} 
                aria-label={t('common.back')}
                className="text-zinc-500 font-black uppercase text-sm tracking-widest hover:text-zinc-900 dark:hover:text-white transition-colors"
              >
                {t('common.back')}
              </button>
              <button 
                disabled={!selectedSlot}
                onClick={() => setStep(4)}
                aria-label={t('booking.finalDetails')}
                className="bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-12 py-4 rounded-2xl font-black shadow-2xl shadow-zinc-200 dark:shadow-black/50 disabled:opacity-50"
              >
                {t('booking.finalDetails')}
              </button>
            </div>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div 
            key="step4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="max-w-2xl mx-auto"
          >
            <div className="bg-white dark:bg-zinc-900 p-10 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500" />
              
              {showAuth ? (
                <div>
                  <Auth 
                    onAuthSuccess={() => setShowAuth(false)} 
                    role="customer" 
                    shopId={selectedShop?.id}
                    shopName={selectedShop?.name}
                  />
                  <button 
                    type="button"
                    onClick={() => setShowAuth(false)} 
                    className="w-full mt-4 text-zinc-400 text-xs font-black uppercase tracking-widest hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    {t('booking.continueAsGuest')}
                  </button>
                </div>
              ) : (
                <form 
                  onSubmit={handleSubmit(() => {
                    setStep(5);
                  })} 
                  className="space-y-6"
                >
                <div className="text-center mb-8">
                  <h3 className={`text-3xl font-black uppercase tracking-tighter mb-2 ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{t('booking.yourInformation')}</h3>
                  <p className={`font-bold ${theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500'}`}>{t('booking.provideDetails')}</p>
                </div>

                {!user && (
                  <div className="mb-8 p-6 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800 text-center">
                    <p className="text-sm font-bold text-zinc-500 dark:text-zinc-300 mb-4">{t('booking.manageBookingsPrompt')}</p>
                    <button
                      type="button"
                      onClick={() => setShowAuth(true)}
                      className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest border-2 border-zinc-200 dark:border-zinc-700 hover:border-zinc-900 dark:hover:border-white transition-colors"
                    >
                      {t('booking.loginOrSignUp')}
                    </button>
                  </div>
                )}

                <div className="max-w-md mx-auto space-y-8">
                  <div className="space-y-4">
                    <div className="relative">
                        <label htmlFor="customer-name" className={`font-black text-zinc-400 dark:text-zinc-300 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.fullName')}</label>
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                          <input
                            id="customer-name"
                            type="text"
                            placeholder={t('booking.fullName')}
                            className={`w-full pl-12 pr-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.name ? 'border-red-500' : 'border-transparent'}`}
                            {...register('name')}
                          />
                        </div>
                        {errors.name && (
                          <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                            <AlertCircle size={10} /> {t(errors.name.message as any)}
                          </p>
                        )}
                      </div>
                      <div className="relative">
                        <label htmlFor="customer-email" className={`font-black text-zinc-400 dark:text-zinc-300 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.emailAddress')}</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                          <input
                            id="customer-email"
                            type="email"
                            placeholder={t('booking.emailAddress')}
                            className={`w-full pl-12 pr-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.email ? 'border-red-500' : 'border-transparent'}`}
                            {...register('email')}
                          />
                        </div>
                        {errors.email && (
                          <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                            <AlertCircle size={10} /> {t(errors.email.message as any)}
                          </p>
                        )}
                      </div>
                      <div className="relative">
                        <label htmlFor="customer-phone" className={`font-black text-zinc-400 dark:text-zinc-300 ml-4 mb-1 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.phoneNumber')}</label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                          <input
                            id="customer-phone"
                            type="tel"
                            placeholder={t('booking.phoneNumber')}
                            className={`w-full pl-12 pr-4 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 rounded-2xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 ${errors.phone ? 'border-red-500' : 'border-transparent'}`}
                            {...register('phone')}
                          />
                        </div>
                        {errors.phone && (
                          <p className="text-[10px] font-black uppercase tracking-widest text-red-500 mt-1 ml-4 flex items-center gap-1">
                            <AlertCircle size={10} /> {t(errors.phone.message as any)}
                          </p>
                        )}
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-5 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/20"
                    >
                      {t('booking.reviewBooking')}
                      <ChevronRight size={20} />
                    </button>
                    <button 
                      type="button"
                      onClick={() => setStep(3)} 
                      className="w-full text-zinc-400 text-xs font-black uppercase tracking-widest hover:text-zinc-900 dark:hover:text-white transition-colors"
                    >
                      {t('booking.changeTime')}
                    </button>
                  </div>
                </form>
              )}
              </div>
            </motion.div>
          )}

        {step === 5 && (
          <motion.div 
            key="step5"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="max-w-3xl mx-auto"
          >
            <div className="bg-white dark:bg-zinc-900 p-10 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500" />
              
              <div className="space-y-8">
                <div className="text-center">
                  <h3 className={`text-3xl font-black uppercase tracking-tighter mb-2 ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{t('booking.confirmYourBooking')}</h3>
                  <p className={`font-bold ${theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500'}`}>{t('booking.reviewBeforeSubmission')}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div className="p-6 bg-zinc-50 dark:bg-zinc-800 rounded-2xl space-y-4">
                      <h4 className="text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 flex items-center gap-2">
                        <Scissors size={14} /> {t('booking.selectedServices')}
                      </h4>
                      <div className="space-y-2">
                        {selectedServices.map(s => (
                          <div key={s.id} className="flex justify-between items-center text-sm">
                            <div className="flex flex-col">
                              <span className="text-zinc-900 dark:text-white font-bold">{s.name}</span>
                              <span className={`font-black text-amber-500 ${language === 'ta' ? 'text-[10px]' : 'text-[9px] uppercase tracking-widest'}`}>{s.category}</span>
                            </div>
                            <span className="font-black text-zinc-500 dark:text-zinc-300">{t('common.currency')} {s.price}</span>
                          </div>
                        ))}
                          <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700 flex justify-between items-center">
                            <span className={`font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('common.total')}</span>
                            <div className="flex items-baseline gap-3">
                              {discount > 0 && (
                                <span className="text-sm font-bold text-zinc-400 dark:text-zinc-300 line-through">{t('common.currency')} {basePrice}</span>
                              )}
                              <span className="text-2xl font-black text-amber-500">{t('common.currency')} {totalPrice}</span>
                              <span className="text-sm font-bold text-zinc-400 dark:text-zinc-300">{totalDuration} {t('common.min')}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-6 bg-zinc-50 dark:bg-zinc-800 rounded-2xl space-y-4">
                        <h4 className="text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 flex items-center gap-2">
                          <Sparkles size={14} /> {t('booking.promoCode')}
                        </h4>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            placeholder="CODE123"
                            className="flex-1 px-4 py-3 bg-white dark:bg-zinc-900 border-2 border-transparent dark:border-zinc-700 rounded-xl focus:border-zinc-900 dark:focus:border-amber-500 outline-none transition-all font-bold uppercase dark:text-white"
                          />
                          <button
                            onClick={handleApplyPromo}
                            className="px-6 py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
                          >
                            {t('booking.apply')}
                          </button>
                        </div>
                      </div>

                    <div className="p-6 bg-zinc-50 dark:bg-zinc-800 rounded-2xl space-y-4">
                      <h4 className={`font-black text-zinc-400 dark:text-zinc-300 flex items-center gap-2 ${language === 'ta' ? 'text-xs' : 'text-xs uppercase tracking-widest'}`}>
                        <Store size={14} /> {t('booking.shopAndTime')}
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <div className="text-lg font-black text-zinc-900 dark:text-white">{selectedShop?.name}</div>
                          <div className="text-sm font-bold text-zinc-500 dark:text-zinc-300 flex items-center gap-1">
                            <MapPin size={12} /> {selectedShop?.address}
                          </div>
                        </div>
                        <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700">
                          <div className="text-sm font-bold text-zinc-900 dark:text-zinc-300 flex items-center gap-2">
                            <CalendarIcon size={14} className="text-amber-500" />
                            {format(parseISO(selectedSlot!.date), 'PPPP')}
                          </div>
                          <div className="text-sm font-bold text-zinc-900 dark:text-zinc-300 flex items-center gap-2 mt-1">
                            <Clock size={14} className="text-amber-500" />
                            {selectedSlot!.time}
                          </div>
                        </div>
                        {selectedBarber && (
                          <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700">
                            <div className="text-sm font-bold text-zinc-900 dark:text-zinc-300 flex items-center gap-2">
                              <UserRound size={14} className="text-amber-500" />
                              {selectedBarber.name}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="p-6 bg-zinc-50 dark:bg-zinc-800 rounded-2xl space-y-4">
                      <h4 className="text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 flex items-center gap-2">
                        <User size={14} /> {t('booking.customerInformation')}
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white dark:bg-zinc-900 rounded-xl text-zinc-400 border dark:border-zinc-700">
                            <User size={16} />
                          </div>
                          <div>
                            <div className={`font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('booking.fullName')}</div>
                            <div className="font-bold text-zinc-900 dark:text-white">{watch('name')}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white dark:bg-zinc-900 rounded-xl text-zinc-400 border dark:border-zinc-700">
                            <Mail size={16} />
                          </div>
                          <div>
                            <div className={`font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('common.email')}</div>
                            <div className="font-bold text-zinc-900 dark:text-white">{watch('email')}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white dark:bg-zinc-900 rounded-xl text-zinc-400 border dark:border-zinc-700">
                            <Phone size={16} />
                          </div>
                          <div>
                            <div className={`font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>{t('common.phone')}</div>
                            <div className="font-bold text-zinc-900 dark:text-white">{watch('phone')}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/30 rounded-2xl space-y-3">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="text-amber-500 shrink-0 mt-0.5" size={18} />
                          <div>
                            <h4 className="font-black text-amber-900 dark:text-amber-400 text-sm mb-1">{t('booking.cancellationPolicy')}</h4>
                            <p className="text-xs font-bold text-amber-700/80 dark:text-amber-400/60 leading-relaxed">
                              {t('booking.cancellationTerms')}
                            </p>
                          </div>
                        </div>
                        <label className="flex items-center gap-3 p-3 bg-white dark:bg-zinc-900 rounded-xl border border-amber-100 dark:border-amber-900/30 cursor-pointer group hover:border-amber-300 transition-colors">
                          <div className="relative flex items-center justify-center">
                            <input 
                              type="checkbox" 
                              className="peer appearance-none w-5 h-5 border-2 border-amber-200 dark:border-amber-900/50 rounded-md checked:bg-amber-500 checked:border-amber-500 transition-colors"
                              checked={acceptedPolicy}
                              onChange={(e) => setAcceptedPolicy(e.target.checked)}
                            />
                            <CheckCircle2 size={12} className="absolute text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" />
                          </div>
                          <span className="text-xs font-black text-amber-900 dark:text-amber-400 select-none group-hover:text-amber-700 dark:group-hover:text-amber-300 transition-colors">
                            {t('booking.acceptPolicy')}
                          </span>
                        </label>
                      </div>

                      <button
                        onClick={handleBooking}
                        disabled={loading || !acceptedPolicy}
                        className="w-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-5 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? t('common.processing') : t('booking.confirmAndBook')}
                        <CheckCircle2 size={20} />
                      </button>
                      <button 
                        onClick={() => setStep(4)} 
                        className="w-full text-zinc-400 text-xs font-black uppercase tracking-widest hover:text-zinc-900 dark:hover:text-white transition-colors"
                      >
                        {t('booking.editDetails')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {step === 6 && (
          <motion.div 
            key="step6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div className="inline-block p-8 bg-amber-50 dark:bg-amber-900/20 text-amber-500 rounded-full mb-8 shadow-inner">
              <CheckCircle2 size={80} />
            </div>
            <h2 className={`text-5xl font-black tracking-tight mb-6 uppercase italic ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{t('booking.youAreSharp')}</h2>
            <p className={`text-lg mb-10 max-w-sm mx-auto font-medium ${theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500'}`}>
              {t('booking.confirmationSent').replace('{email}', watch('email'))} 
              {t('booking.seeYouAt')
                .replace('{shop}', selectedShop?.name || '')
                .replace('{date}', format(parseISO(selectedSlot!.date), 'PPPP', { locale: dateLocale }))
                .replace('{time}', selectedSlot!.time)}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleDownloadICS}
                className="bg-amber-500 text-white px-8 py-4 rounded-2xl font-black shadow-xl shadow-amber-500/20 hover:scale-105 transition-transform flex items-center gap-2 uppercase tracking-widest text-xs"
              >
                <CalendarPlus size={18} /> Add to Calendar
              </button>
              <button
                onClick={() => {
                  setStep(0);
                  setSelectedShop(null);
                  setSelectedBarber(null);
                  setSelectedServices([]);
                  setSelectedSlot(null);
                }}
                className="bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-12 py-4 rounded-2xl font-black shadow-2xl shadow-zinc-200 dark:shadow-black/50 hover:scale-105 transition-transform uppercase tracking-widest text-xs"
              >
                {t('booking.bookAnother')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </>
      ) : (
        <motion.div
          key="history"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {user && (() => {
            const confirmedReservations = myReservations.filter(r => r.status === 'confirmed' || r.status === 'pending'); // include pending for total upcoming
            const pastReservations = confirmedReservations.filter(r => r.reservation_slots?.[0]?.slots?.date && new Date(r.reservation_slots[0].slots.date) < new Date());
            const totalSpent = pastReservations.reduce((sum, r) => sum + Number(r.total_price || 0), 0);
            const loyaltyPoints = Math.floor(totalSpent * 1); // 1 point per $ spent
            const loyaltyTier = loyaltyPoints >= 1000 ? 'Platinum' : loyaltyPoints >= 500 ? 'Gold' : loyaltyPoints >= 100 ? 'Silver' : 'Bronze';
            
            return (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className={`${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} p-8 rounded-[3rem] border shadow-xl flex flex-col items-center text-center lg:col-span-1`}>
                  <div className="w-24 h-24 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-full flex items-center justify-center text-4xl font-black mb-6 shadow-2xl overflow-hidden shrink-0">
                    {(profile?.email?.[0] || user.email?.[0] || '?').toUpperCase()}
                  </div>
                  <h3 className={`text-2xl font-black uppercase tracking-tighter truncate w-full ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>
                    {profile?.email?.split('@')[0] || user.email?.split('@')[0] || 'User'}
                  </h3>
                  <p className="text-zinc-500 font-bold truncate dark:text-zinc-300 mb-6 w-full">{user.email}</p>
                  
                  <div className="w-full space-y-4">
                    <div className="p-4 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl flex justify-between items-center">
                      <span className="text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">Tier</span>
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        loyaltyTier === 'Platinum' ? 'bg-purple-100 text-purple-600' :
                        loyaltyTier === 'Gold' ? 'bg-amber-100 text-amber-600' :
                        loyaltyTier === 'Silver' ? 'bg-zinc-200 text-zinc-600' :
                        'bg-orange-100 text-orange-600'
                      }`}>
                        {loyaltyTier}
                      </span>
                    </div>
                    <div className="p-4 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl flex justify-between items-center">
                      <span className="text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">Loyalty Points</span>
                      <span className="font-black text-zinc-900 dark:text-white">{loyaltyPoints}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 lg:col-span-2">
                  <div className={`${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} p-8 rounded-[3rem] border shadow-xl flex flex-col justify-center`}>
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mb-6">
                      <CalendarDays size={24} />
                    </div>
                    <p className="text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-2">Total Bookings</p>
                    <h3 className={`text-4xl font-black tracking-tighter ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{myReservations.length}</h3>
                  </div>

                  <div className={`${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} p-8 rounded-[3rem] border shadow-xl flex flex-col justify-center`}>
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mb-6">
                      <Star size={24} />
                    </div>
                    <p className="text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-2">Favorite Barber</p>
                    <h3 className={`text-xl font-black uppercase tracking-tighter truncate ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>
                      {(() => {
                        const counts = myReservations.reduce((acc, r: any) => {
                          const barberName = r.barbers?.name;
                          if (barberName) acc[barberName] = (acc[barberName] || 0) + 1;
                          return acc;
                        }, {} as {[key: string]: number});
                        const favorite = Object.entries(counts).sort((a, b) => (b[1] as number) - (a[1] as number))[0];
                        return favorite ? favorite[0] : 'None yet';
                      })()}
                    </h3>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="text-center mb-12">
            <h2 className={`text-3xl font-black uppercase tracking-tighter mb-4 ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{t('booking.yourBookings')}</h2>
            <p className={`font-bold ${theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500'}`}>{t('booking.manageAppointments')}</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-zinc-200 dark:border-zinc-800 border-t-zinc-900 dark:border-t-amber-500 rounded-full animate-spin" />
            </div>
          ) : myReservations.length > 0 ? (
            <div className="grid grid-cols-1 gap-6">
              {myReservations.map((res) => {
                const slot = res.reservation_slots?.[0]?.slots;
                const isCancelled = res.status === 'cancelled';
                const isPastDate = slot ? new Date(`${slot.date}T${slot.time}`) < new Date() : false;

                return (
                  <div 
                    key={res.id}
                    className={`${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} p-8 rounded-[3rem] border shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden ${isCancelled ? 'opacity-60' : ''}`}
                  >
                    {isCancelled && (
                      <div className="absolute top-0 left-0 w-full h-full bg-zinc-50/50 dark:bg-zinc-950/50 flex items-center justify-center z-10">
                        <span className={`bg-red-500 text-white px-6 py-2 rounded-full font-black -rotate-12 ${language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[10px]'}`}>{t('common.cancelled')}</span>
                      </div>
                    )}
                    
                      <div className="flex items-center gap-6">
                        <div className={`p-5 rounded-2xl ${isCancelled ? (theme === 'dark' ? 'bg-zinc-800 text-zinc-600' : 'bg-zinc-100 text-zinc-400') : (theme === 'dark' ? 'bg-amber-900/20 text-amber-500' : 'bg-amber-50 text-amber-500')}`}>
                          <CalendarIcon size={32} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`text-xl font-black uppercase tracking-tighter ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{res.barber_shops?.name}</h3>
                            <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-widest ${
                              res.status === 'confirmed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
                              res.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
                              'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            }`}>
                              {res.status === 'pending' ? t('common.pending') : res.status === 'confirmed' ? t('common.confirmed') : t('common.cancelled')}
                            </span>
                          </div>
                          <div className={`flex flex-wrap gap-4 text-sm font-bold ${theme === 'dark' ? 'text-zinc-300' : 'text-zinc-500'}`}>
                            <div className="flex items-center gap-1">
                              <CalendarIcon size={14} /> {slot ? format(parseISO(slot.date), 'PPP', { locale: dateLocale }) : t('common.na')}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock size={14} /> {slot?.time ? slot.time.substring(0, 5) : t('common.na')}
                            </div>
                            {res.barbers && (
                              <div className="flex items-center gap-1 text-amber-500 dark:text-amber-400">
                                <UserRound size={14} /> {res.barbers.name}
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Scissors size={14} /> {res.reservation_services?.map((s: any) => s.services?.name).join(', ')}
                            </div>
                          </div>
                        </div>
                      </div>

                    <div className="flex items-center gap-4">
                        <div className="text-right mr-4">
                          <div className={`text-xs font-bold uppercase tracking-widest ${theme === 'dark' ? 'text-zinc-300' : 'text-zinc-400'}`}>{t('common.total')}</div>
                          <div className={`text-xl font-black ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{t('common.currency')} {res.total_price}</div>
                        </div>
                      {!isCancelled && isPastDate && res.status === 'confirmed' && (
                        <button
                          onClick={() => setReviewingReservation(res)}
                          className="bg-amber-500 text-white px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-amber-400 transition-all"
                        >
                          {t('booking.leaveReview')}
                        </button>
                      )}
                      {isPastDate && (
                        <button
                          onClick={() => {
                            if (res.barber_shop_id) {
                              setSelectedShop(shops.find(s => s.id === res.barber_shop_id) || null);
                              if (res.barber) {
                                setSelectedBarber(res.barber);
                              }
                              setStep(3); // Go to service selection
                              setMode('booking');
                            }
                          }}
                          className={`${theme === 'dark' ? 'bg-zinc-800 text-white hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-900 hover:bg-zinc-200'} px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all`}
                        >
                          {t('booking.rebook') || 'Rebook'}
                        </button>
                      )}
                      {!isCancelled && !isPastDate && (
                        <button
                          onClick={() => setConfirmCancel({ resId: res.id, slotId: res.reservation_slots?.[0]?.slot_id })}
                          className="bg-zinc-50 dark:bg-zinc-800 text-zinc-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-500 p-4 rounded-2xl transition-all"
                          title={t('booking.cancelBooking')}
                          aria-label={t('booking.cancelBooking')}
                        >
                          <X size={20} />
                        </button>
                      )}
                      <button
                        onClick={() => setViewingDetails(res)}
                        className="bg-zinc-50 dark:bg-zinc-800 text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-600 dark:hover:text-zinc-200 p-4 rounded-2xl transition-all"
                        title={t('common.view') || 'View Details'}
                        aria-label={t('common.view') || 'View Details'}
                      >
                        <Eye size={20} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`text-center py-20 ${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} rounded-[3rem] border border-dashed`}>
              <div className="inline-block p-6 bg-zinc-50 dark:bg-zinc-800 text-zinc-300 dark:text-zinc-300 rounded-full mb-6">
                <History size={48} />
              </div>
              <h3 className={`text-2xl font-black uppercase tracking-tighter mb-2 ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{t('booking.noBookings')}</h3>
              <p className="text-zinc-400 font-bold mb-8 dark:text-zinc-300">{t('booking.manageAppointments')}</p>
              <button 
                onClick={() => setMode('booking')}
                className="bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-8 py-4 rounded-2xl font-black shadow-xl shadow-zinc-200 dark:shadow-black/50"
              >
                {t('booking.bookNow')}
              </button>
            </div>
          )}
        </motion.div>
      )}

      <AnimatePresence>
        {viewingDetails && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setViewingDetails(null)}
              className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`relative w-full max-w-xl bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-zinc-100 dark:border-zinc-800 transition-colors`}
            >
              <div className="p-6 sm:p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between shrink-0">
                <div>
                  <h3 className={`text-xl sm:text-2xl font-black uppercase tracking-tighter ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>{t('admin.reservationDetails') || 'Reservation Details'}</h3>
                  <div className={`inline-flex px-2 py-0.5 mt-2 rounded-md text-[10px] font-black uppercase tracking-widest ${
                    viewingDetails.status === 'confirmed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
                    viewingDetails.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
                    'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                  }`}>
                    {viewingDetails.status === 'pending' ? t('common.pending') : viewingDetails.status === 'confirmed' ? t('common.confirmed') : t('common.cancelled')}
                  </div>
                </div>
                <button
                  onClick={() => setViewingDetails(null)}
                  className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 dark:text-zinc-300"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar">
                <div className="space-y-4 font-bold text-sm">
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-transparent dark:border-zinc-800/50">
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-1">{t('admin.shop') || 'Shop'}</div>
                    <div className={theme === 'dark' ? 'text-zinc-200' : 'text-zinc-800'}>{viewingDetails.barber_shops?.name}</div>
                  </div>
                  {viewingDetails.barbers && (
                    <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-transparent dark:border-zinc-800/50">
                      <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-1">{t('admin.barber') || 'Barber'}</div>
                      <div className={theme === 'dark' ? 'text-zinc-200' : 'text-zinc-800'}>{viewingDetails.barbers.name}</div>
                    </div>
                  )}
                  <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-transparent dark:border-zinc-800/50">
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-1">{t('admin.services') || 'Services'}</div>
                    <div className={theme === 'dark' ? 'text-zinc-200' : 'text-zinc-800'}>{viewingDetails.reservation_services?.map((s: any) => s.services?.name).join(', ')}</div>
                  </div>
                  <div className="flex gap-4">
                    <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-transparent dark:border-zinc-800/50 flex-1">
                      <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-1">{t('admin.date') || 'Date'}</div>
                      <div className={theme === 'dark' ? 'text-zinc-200' : 'text-zinc-800'}>
                        {viewingDetails.reservation_slots?.[0]?.slots?.date ? format(parseISO(viewingDetails.reservation_slots[0].slots.date), 'PPP', { locale: dateLocale }) : t('common.na')}
                      </div>
                    </div>
                    <div className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-transparent dark:border-zinc-800/50 flex-1">
                      <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-1">{t('admin.time') || 'Time'}</div>
                      <div className={theme === 'dark' ? 'text-zinc-200' : 'text-zinc-800'}>{viewingDetails.reservation_slots?.[0]?.slots?.time ? viewingDetails.reservation_slots[0].slots.time.substring(0, 5) : t('common.na')}</div>
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/10 flex justify-between items-center border border-amber-100/50 dark:border-amber-900/20">
                    <div className="text-amber-600/60 dark:text-amber-500/60 font-black uppercase tracking-widest text-[10px]">{t('common.total')}</div>
                    <div className="text-xl font-black text-amber-600 dark:text-amber-500">{t('common.currency')} {viewingDetails.total_price}</div>
                  </div>
                </div>

                {viewingDetails.notes && (
                  <div className="p-6 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-700/30">
                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-2">{t('common.notes') || 'Notes'}</div>
                    <div className="font-bold text-zinc-900 dark:text-zinc-200 leading-relaxed text-sm italic">"{viewingDetails.notes}"</div>
                  </div>
                )}

                <div className="pt-4">
                  <button
                    onClick={() => setViewingDetails(null)}
                    className="w-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-5 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50"
                  >
                    {t('common.close') || 'Close'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {reviewingReservation && (
        <ReviewModal
          isOpen={!!reviewingReservation}
          onClose={() => setReviewingReservation(null)}
          reservationId={reviewingReservation.id}
          barberId={reviewingReservation.barber_id}
          customerId={user.id}
          onSuccess={() => fetchMyReservations()}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirmCancel}
        onClose={() => setConfirmCancel(null)}
        onConfirm={() => {
          if (confirmCancel) {
            handleCancelBooking(confirmCancel.resId, confirmCancel.slotId);
          }
        }}
        title={t('booking.cancelBooking')}
        message={t('booking.cancelConfirm')}
        variant="danger"
      />
    </div>
  );
}
